import { useQuery } from '@tanstack/react-query'
import { type components } from '@akillidernek/api-client'
import { ApiError } from '@akillidernek/api-client'
import { Badge, Button, Card, CardBody, CardHeader } from '@akillidernek/ui'
import { api } from '../api'
import { tenantSlug } from '../tenant'

type Uyelik = components['schemas']['PortalMeRead']

const DURUM: Record<
  Uyelik['status'],
  { etiket: string; ton: 'info' | 'success' | 'warning' | 'danger' | 'neutral'; aciklama: string }
> = {
  pending: {
    etiket: 'Başvuru değerlendiriliyor',
    ton: 'info',
    aciklama: 'Yönetim kurulu kararını verdiğinde burada göreceksiniz.',
  },
  active: { etiket: 'Aktif üye', ton: 'success', aciklama: 'Üyeliğiniz sürüyor.' },
  passive: {
    etiket: 'Pasif',
    ton: 'warning',
    aciklama: 'Üyeliğiniz durdu. Ayrıntı için dernekle iletişime geçin.',
  },
  resigned: { etiket: 'İstifa', ton: 'neutral', aciklama: 'Üyeliğiniz istifa ile sona erdi.' },
  expelled: { etiket: 'İhraç', ton: 'danger', aciklama: 'Üyeliğiniz sona erdi.' },
  deceased: { etiket: 'Kayıt kapalı', ton: 'neutral', aciklama: '' },
  rejected: {
    etiket: 'Başvuru reddedildi',
    ton: 'danger',
    aciklama: 'Yönetim kurulu başvurunuzu uygun bulmadı.',
  },
}

const tarih = (deger: string | null | undefined) =>
  deger ? new Date(deger).toLocaleDateString('tr-TR') : '—'

/** Üyenin kendi kaydı — YES-159. Başvuru sonrası "ne oldu?" sorusunun yeri. */
export function Uyeligim({ onBasvur }: { onBasvur: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-me', tenantSlug],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/portal/me')
      return data
    },
  })

  if (isLoading) return <p className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</p>
  // Hesabı var ama bu dernekte kaydı yok: başka bir dernekte üye olan biri ya
  // da panel yöneticisi. Çıkmaz sokak bırakmıyoruz — başvuru buradan yapılır.
  if (error instanceof ApiError && error.problem.status === 404)
    return (
      <Card>
        <CardHeader
          title="Bu dernekte kaydınız yok"
          description="Hesabınız var ama bu derneğe henüz üye değilsiniz."
        />
        <CardBody className="flex flex-col items-start gap-3 text-sm">
          <p>Üyelik başvurusu yaparsanız yönetim kurulu değerlendirir.</p>
          <Button onClick={onBasvur}>Üyelik başvurusu yap</Button>
        </CardBody>
      </Card>
    )
  if (error || !data)
    return (
      <Card>
        <CardBody className="text-sm">Üyelik kaydınız yüklenemedi.</CardBody>
      </Card>
    )

  const durum = DURUM[data.status]
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={`${data.first_name} ${data.last_name}`}
          description={data.membership_type_name}
          action={<Badge tone={durum.ton}>{durum.etiket}</Badge>}
        />
        <CardBody className="flex flex-col gap-3">
          {durum.aciklama && <p className="text-sm">{durum.aciklama}</p>}
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Satir baslik="Üye no" deger={data.member_no ?? '—'} />
            <Satir baslik="E-posta" deger={data.email ?? '—'} />
            <Satir baslik="Telefon" deger={data.phone ?? '—'} />
            <Satir baslik="Başvuru tarihi" deger={tarih(data.applied_at)} />
            <Satir baslik="Karar tarihi" deger={tarih(data.board_decision_date)} />
            <Satir baslik="Üyelik başlangıcı" deger={tarih(data.started_at)} />
            {data.ended_at && <Satir baslik="Üyelik bitişi" deger={tarih(data.ended_at)} />}
          </dl>
        </CardBody>
      </Card>

      {Object.keys(data.custom_fields).length > 0 && (
        <Card>
          <CardHeader title="Dernek bilgileri" />
          <CardBody>
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {Object.entries(data.custom_fields).map(([anahtar, deger]) => (
                <Satir key={anahtar} baslik={anahtar} deger={String(deger ?? '—')} />
              ))}
            </dl>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

function Satir({ baslik, deger }: { baslik: string; deger: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-[var(--ad-text-muted)]">{baslik}</dt>
      <dd>{deger}</dd>
    </div>
  )
}
